import {
  Box,
  Chip,
  CircularProgress,
  makeStyles,
  PersonIcon,
  Theme,
  Tooltip,
  Typography,
} from '@freighthub/silhouette-core'
import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import { useShipmentDetails } from 'features/ShipmentDetails'
import { FC, useEffect, useState } from 'react'
import { useRootStore } from 'service/useRootStore'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'
import { formatMomentShort } from 'utils/date'
import { useI18n } from 'utils/localisation'

import { DOCUMENT_ACTION_TYPE, DocumentActionButton } from '../DocumentActionButton'
import { DocumentPreview, getPreviewUrl } from '../DocumentPreview'
import { DocumentInterface } from '../Documents'
import { DocumentType } from '../domain/Document'
import { useDocumentTypeState } from '../service/useDocumentTypes'
import { FileUploadButton } from './FileUploadButton'

type StyleProps = {
  isDeleting: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  container: ({ isDeleting }) => ({
    opacity: isDeleting ? 0.5 : 1,
  }),
  nestedDocument: {
    borderRadius: 2,
    position: 'absolute',
    left: 50,
    backgroundColor: theme.palette.divider,
  },
  verticalSection: {
    width: 2,
    height: 48,
    bottom: '50%',
  },
  horizontalSection: {
    width: 20,
    height: 2,
    top: '50%',
  },
  userProvided: {
    margin: theme.spacing(0, 1, 0, 8.5),
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 32,
    lineHeight: theme.typography.h5.lineHeight,
    width: 32,
    backgroundColor: theme.palette.divider,
    borderRadius: '50%',
  },
  documentDate: {
    color: theme.palette.grey[600],

    '&::before': {
      content: '"\u00b7"',
      margin: theme.spacing(0, 0.5),
      color: theme.palette.grey[600],
    },
  },
  tag: {
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontSize: theme.typography.caption.fontSize,
    padding: theme.spacing(0.5, 1),
    lineHeight: theme.typography.body3.lineHeight,
    borderRadius: 4,
    width: 120,
    marginRight: theme.spacing(1),
    zIndex: 1,
  },
  versionChip: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    margin: theme.spacing(0, 0.5),
  },
}))

type DocumentItemProps = UserDocumentItemProps | NonUserDocumentItemProps

type CommonDocumentItemProps = {
  url: string
  doc: DocumentInterface
  inputName: string
  uploadFile?: (file: File) => void
  deleteFile?: (doc: DocumentInterface) => Promise<void> | void
  isUserDoc?: boolean
  e2eTestId?: string
  allowedFormats?: string[]
  onDownloadClick?: (url: string, docName: string) => void
  onPreview?: () => void
  onPreviewClose?: () => void
  showDownloadButton?: boolean
}

type UserDocumentItemProps = CommonDocumentItemProps & {
  isUserDoc: true
}

type NonUserDocumentItemProps = CommonDocumentItemProps & {
  isUserDoc?: false
}

export const DocumentItem: FC<DocumentItemProps> = ({
  url,
  doc,
  uploadFile,
  deleteFile,
  e2eTestId,
  inputName,
  allowedFormats,
  onDownloadClick,
  onPreview,
  onPreviewClose,
  showDownloadButton = true,
  ...restProps
}) => {
  const { containerNumbers } = doc
  const { shipment } = useShipmentDetails()
  const [docUrl, setDocUrl] = useState<string>(url)
  const [isDeleting, setIsDeleting] = useState(false)
  const [documentType, setDocumentType] = useState<DocumentType | undefined>()
  const { translate } = useI18n()
  const classes = useStyles({ isDeleting })
  const { documentTypes } = useDocumentTypeState()
  const documentDate = formatMomentShort(doc.createdAt)

  const { isEnabled } = useUnleashFeatureFlags()
  const isDocumentV2Enabled = isEnabled(UnleashFeatureFlagKeys.DOCUMENTS_V2)
  const showVersionTag = isDocumentV2Enabled && Boolean(doc.publishedVersion)
  const { API } = useRootStore()

  const query = useQuery({
    queryKey: ['documentURL', doc._id],
    queryFn: () => API.documents.getDocumentPreview('shipment', shipment._id, doc._id),
    enabled: isDocumentV2Enabled,
  })

  const onDelete = async () => {
    if (!isDeleting) {
      setIsDeleting(true)
      deleteFile?.(doc)?.then(() => {
        setIsDeleting(false)
      })
    }
  }

  useEffect(() => {
    const getDocumentType = () => {
      return documentTypes.find((type) => {
        return doc.role === type.code
      })
    }

    if (documentTypes) {
      setDocumentType(getDocumentType())
    }
  }, [documentTypes, doc])

  useEffect(() => {
    setDocUrl(url)
  }, [url])

  useEffect(() => {
    if (query.data) {
      const updatedUrl = URL.createObjectURL(new Blob([query.data], { type: doc.mimeType }))
      setDocUrl(updatedUrl)
    }
  }, [query.data])

  // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
  const translatedDocumentTypeDisplayName = translate(documentType?.code)
  const documentDisplayName =
    translatedDocumentTypeDisplayName === documentType?.code
      ? documentType?.displayName
      : translatedDocumentTypeDisplayName

  return (
    <Box display="flex" alignItems="center" className={classes.container} data-e2e-id={e2eTestId}>
      {restProps.isUserDoc === true ? (
        <Box position="relative" left={0}>
          <div className={cn(classes.nestedDocument, classes.verticalSection)} />
          <div className={cn(classes.nestedDocument, classes.horizontalSection)} />
          <div className={classes.userProvided}>
            <PersonIcon />
          </div>
        </Box>
      ) : (
        <div className={classes.tag}>{documentDisplayName}</div>
      )}
      <Box display="flex" flexGrow={1}>
        <Tooltip
          title={
            containerNumbers && containerNumbers.length > 0
              ? `${translate('shipment_details-documents-container_numbers')}: ${containerNumbers.join(', ')}`
              : ''
          }
          arrow
          placement="top"
        >
          <Box display="flex">
            <Typography>
              {doc.name}{' '}
              {showVersionTag && (
                <Chip
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  label={`v${doc.publishedVersion}`}
                  size="small"
                  variant="outlined"
                  className={classes.versionChip}
                  data-testid="doc-version"
                />
              )}
            </Typography>
            {!restProps.isUserDoc && <Typography className={classes.documentDate}>{documentDate}</Typography>}
          </Box>
        </Tooltip>
      </Box>
      {isDeleting && <CircularProgress />}
      <DocumentPreview
        onPreview={onPreview}
        onPreviewClose={onPreviewClose}
        documentSource={getPreviewUrl(docUrl)}
        data-tracking-id="shipment-document-preview"
      />
      {showDownloadButton &&
        (onDownloadClick ? (
          <DocumentActionButton
            title={translate('documents-download_file')}
            actionType={DOCUMENT_ACTION_TYPE.DOWNLOAD}
            onClick={() => onDownloadClick(docUrl, doc.name)}
            data-e2e-id="shipment-document-download"
            href={docUrl}
            target="_blank"
          />
        ) : (
          <DocumentActionButton
            title={translate('documents-download_file')}
            actionType={DOCUMENT_ACTION_TYPE.DOWNLOAD}
            target="_blank"
            data-tracking-id="shipment-document-download"
            data-e2e-id="shipment-document-download"
            href={docUrl}
          />
        ))}
      {uploadFile && (
        <FileUploadButton
          uploadFile={uploadFile}
          inputName={inputName}
          tooltip={translate('documents-upload_replace')}
          allowedFormats={allowedFormats}
        />
      )}
      {doc.userProvided && deleteFile && (
        <DocumentActionButton
          title={translate('documents-delete_file')}
          data-tracking-id="shipment-document-delete"
          actionType={DOCUMENT_ACTION_TYPE.DELETE}
          onClick={onDelete}
        />
      )}
    </Box>
  )
}
